import $ from 'jquery';
import { getElementRectNoReflow, whenElementInView } from '../../util/intersection/async.js';
import { loadPartial } from '../../remote/partial-content.js';
import Section from '../../remote/section.js';
import { handleRemoteResponse } from '../../remote/remote-response.js';

/**
 * @typedef {{
 *  url?: string;
 *  data?: { [key: string]: any; };
 * 	method?: 'GET'|'POST';
 *  requestDataType?: RequestDataType;
 *  selector?: string;
 *  container?: string|HTMLElement|JQuery;
 *  buttonContainer?: string|HTMLElement|JQuery;
 *  empty?: boolean;
 *  replace?: boolean;
 *  allowRestore?: boolean;
 *  animation?: boolean;
 *  loader?: boolean;
 *  triggerElement?: HTMLElement|JQuery;
 *  initCallback?: ($container: JQuery) => void;
 * }} LoadFragmentOptions
 */

/** The Section object */
const section = new Section();

/**
 * Handler is assigned to multiple events
 *
 * @param {JQuery.TriggeredEvent} e
 */
const loadFragmentHandler = async (e) => {

	// Disable other actions
	e.preventDefault();
	e.stopPropagation();
	e.stopImmediatePropagation();

	try {
		// @ts-ignore
		await mpro.loadFragment(e.currentTarget, e.detail);
	}
	catch (error) {
		console.warn(error);
	}
};

/**
 * @param  {[import('../../remote/section').MpSectionId[], import('../../remote/section').MpSectionOptions?]} args
 */
mpro.sectionReload = (...args) => section.reload(...args);

/**
 * @param  {[import('../../remote/section').MpSectionId[], import('../../remote/section').MpSectionOptions?]} args
 */
mpro.sectionReloadDelayed = (...args) => section.reloadDelayed(...args);

/**
 * @param  {[string | HTMLElement]} args
 */
mpro.sectionReloadContainer = (...args) => section.reloadContainer(...args);

/**
 * Process special actions bound on form or form elements
 *
 * @param {JQuery<HTMLElement>|HTMLElement} container
 *
 * @returns {void}
 */
mpro.handleSectionReload = (container) => {

	/** @type {JQuery<HTMLElement>} */
	// @ts-ignore
	const $container = (container instanceof $) ? container : $(container);

	// Add sections
	const $sections = $container.find('[data-section-id]').add($container.filter('[data-section-id]'));

	$sections.on('mp-section-reload.section-reload-handler', function(/** @type {JQuery.TriggeredEvent} */e, url, data) {
		e.stopPropagation();
		e.stopImmediatePropagation();

		// @ts-ignore
		const $elem = $(e.currentTarget);
		/** @type {{ [key: string]: any; sectionId: string; }} */ // @ts-ignore
		const elemData = $elem.data();
		const { sectionId, sectionShowLoader: showLoader } = elemData;

		if (!sectionId) {
			console.warn('Cannot reload section for element (%o) - missing section id', this);
		}

		// Reload the section with delay (also filter out undefined values)
		section.reloadDelayed([sectionId], Object.fromEntries(Object.entries({ url, data, showLoader }).filter(([_, v]) => v !== undefined)));
	});
};

/**
 * @param {JQuery<HTMLElement>|HTMLElement} container
 */
mpro.handleFragmentLoad = (container) => {

	/** @type {JQuery<HTMLElement>} */
	// @ts-ignore
	const $container = (container instanceof $) ? container : $(container);
	const triggerSelector = '[data-load-fragment]';

	// Get all triggers
	const $triggers = $container.find(triggerSelector).add($container.filter(triggerSelector));
	// Filter click triggers
	const $clickTriggers = $triggers.filter('a, button');
	// Rest of the triggers are dynamic (will trigger manually/programmatically)
	const $dynamicTriggers = $triggers.not($clickTriggers);
	// Get triggers that have auto-click when in viewport
	const $triggersInViewport = $triggers.filter('[data-load-fragment-in-view]');

	// Trigger on click
	$clickTriggers.on('click.loadFragment', loadFragmentHandler);
	// Trigger on custom event
	$dynamicTriggers.on('mp-load-fragment.loadFragment', loadFragmentHandler);

	// Handle auto-viewport triggers
	for (const trigger of $triggersInViewport) {

		const $trigger = $(trigger);
		const offset = $trigger.data('load-fragment-in-view') || 200;
		const $overlayBody = $container.closest('div[data-mp-overlay-body]');
		let root;

		// Use the overlay body as the scroll container
		if ($overlayBody.length > 0) {
			root = $overlayBody[0];
		}

		// @ts-ignore
		whenElementInView(trigger, {
			root,
			rootMargin: `0px 0px ${offset}px 0px`,
		}).then(() => {
			// Trigger click
			if ((trigger instanceof HTMLAnchorElement) || (trigger instanceof HTMLButtonElement)) {
				trigger.click();
			}
			// Trigger send custom event
			else {
				$trigger.triggerNative('mp-load-fragment');
			}
		});
	}
};

/**
 * @param {HTMLElement | JQuery} reloadElement
 * @param {LoadFragmentOptions} [options]
 */
mpro.loadFragment = async (reloadElement, options = {}) => {

	const $reloadElement = $(reloadElement);
	reloadElement = $reloadElement[0];
	options = {
		...$reloadElement.data('load-fragment'),
		...options,
	};

	const {
		url,
		method = 'GET',
		data,
		requestDataType,
		selector: contentSelector,
		container: containerSelector,
		buttonContainer: buttonContainerSelector,
		allowRestore = false,
		replace = true,
		empty = true,
		loader = false,
		animation = true,
		triggerElement,
		initCallback,
	} = options;

	// TODO: add typeCheckConfig validation for each option

	// console.log('options', options);

	const $triggerElement = $(triggerElement);

	/**
	 * Get the loading context (the container)
	 *
	 * @type {JQuery<HTMLElement>}
	 */
	// @ts-ignore
	const $context = $(containerSelector ?? $reloadElement);
	let contextData = $context.data();

	if (!contextData) {
		contextData = {};
		$context.data(contextData);
	}

	// Another loading is already in progress
	if (contextData._loadFragmentInProgress === true) {
		throw new Error('Fragment load already in progress');
	}

	// Set in-progress mode
	contextData._loadFragmentInProgress = true;

	const $originalButtonContainer = buttonContainerSelector ? $reloadElement.closest(buttonContainerSelector) : $();

	// Get element dimensions without causing a reflow
	const triggerRect = await getElementRectNoReflow(reloadElement);
	const isButtonLoader = ($reloadElement.is('a, button') || (triggerRect.width < 48 || triggerRect.height < 48));

	// Disable the trigger button
	($triggerElement.length > 0) && mpro.addButtonDisabled($triggerElement);

	// Show button loader
	if (isButtonLoader) {
		mpro.addButtonLoading($reloadElement);
	}
	// Show content loader
	else {
		mpro.addContentLoader($reloadElement, 'auto');
	}

	try {
		// Load the partial content in place
		const partial = await loadPartial($context, {
			type: 'fragment',
			url: url || $reloadElement.attr('href'),
			method,
			data,
			requestDataType,
			loader,
			animation,
			replaceContainer: ((replace === true) || ((empty === true) && ($context[0] === $reloadElement[0]))) ? true : false,
			emptyContainer: empty,
			contentSelector,
			strictContentSelector: true,
			restoreOriginal: (allowRestore === true),
			initCallback,
			relatedTarget: $reloadElement,
		});

		// Got redirect location
		if (partial.location) {

			// Close everything and return
			mpro.closeAllOverlays();

			// Handle extra response fields using the standard handler
			try {
				return void handleRemoteResponse(partial);
			}
			catch (error) {
				console.warn('Error handling remote response: %o', error);
			}
		}

		// Handle extra response fields using the standard handler
		try {
			handleRemoteResponse(partial);
		}
		catch (error) {
			console.warn('Error handling remote response: %o', error);
		}

		if (
			// Re-parent the trigger
			partial.$content?.length &&
			// Replace the whole button container
			(buttonContainerSelector && ($originalButtonContainer.length > 0))
		) {
			let $buttonContainer = partial.$content.filter(buttonContainerSelector).first();
			if ($buttonContainer.length === 0) {
				$buttonContainer = partial.$content.find(buttonContainerSelector).first();
			}

			// Use the new button container
			if ($buttonContainer.length > 0) {
				$originalButtonContainer.replaceWith($buttonContainer);
			}
			// No buttons container found - remove it
			else {
				$originalButtonContainer.remove();
			}
		}

		// Remove the trigger (in most cases this trigger is already removed by the content that is replaced with)
		// And we must use detach to preserve the event listeners
		if (allowRestore === true) {

			// ? ---------------------------------------------------------------------------------------------------------------
			// ? This processing is done for the $trigger (not the $context/$container element, which is handled by loadPartial)
			// ? ---------------------------------------------------------------------------------------------------------------

			let $next;
			let $parent;
			const $prev = $reloadElement.prev();

			if ($prev.length === 0) {
				$next = $reloadElement.next();
				if ($next.length === 0) {
					$parent = $reloadElement.parent();
				}
			}

			$reloadElement.detach();

			if (partial.$content) {
				partial.$content.find('[data-restore-original]').one('click', (e) => {
					if ($prev && $prev.length > 0) {
						$reloadElement.insertAfter($prev);
					}
					else if ($next && $next.length > 0) {
						$reloadElement.insertBefore($next);
					}
					else if ($parent && $parent.length > 0) {
						$parent.append($reloadElement);
					}
				});
			}
		}
		else {
			$reloadElement.remove();
		}

		return partial;
	}
	finally {

		// Remove in-progress mode
		delete contextData._loadFragmentInProgress;

		// Enable the trigger button
		($triggerElement.length > 0) && mpro.removeButtonDisabled($triggerElement);

		// Remove the button loader
		if (isButtonLoader) {
			mpro.removeButtonLoading($reloadElement);
		}
		// Remove the content loader
		else {
			mpro.removeContentLoader($reloadElement);
		}
	}
};

// Add init function that is executed on every page/fragment render
mpro.addInitStatic(($container) => {

	// Section reload handler
	mpro.handleSectionReload($container);

	// Fragment load handler
	mpro.handleFragmentLoad($container);
});

export default mpro;
